import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useMutation } from 'react-query';

import Notify from 'store/utils/Notify';
import ErrorHandler from 'store/utils/ErrorHandler';
import { getIntentsRequest } from 'store/services/intents/api';
import { intentType } from 'types';
import ReplaceAllModal from 'components/ReplaceAllModal';
import SelectableList from 'components/SelectableList';
import IntentItem from './components/IntentItem';
import replaceIntentRequest from './api';

const IntentsList = ({
  intents,
  selectedIntent,
  currentIntentIndex,
  selectIntent,
  deselectIntent,
  toggleIntentStatus,
  duplicateIntent,
}) => {
  const [intentToReplace, setIntentToReplace] = useState(undefined);
  const openModal = id => setIntentToReplace(id);
  const closeModal = () => setIntentToReplace(undefined);
  const [replaceIntent] = useMutation(replaceIntentRequest, {
    onSuccess: ({ data }) => {
      Notify.success({
        title: 'Intent was replaced successfully',
        description: ErrorHandler.getDetailError({ response: { data } }),
      });
    },
    onError: error => {
      Notify.error({
        title: 'Failed to replace intent',
        description: ErrorHandler.getDetailError(error),
      });
    },
    onSettled: closeModal,
  });
  const [fetchIntents, { isLoading }] = useMutation(async term => {
    const { data } = await getIntentsRequest({
      name__icontains: term,
    });
    return data?.results?.map(({ name, id, archived }) => ({
      label: name,
      value: id,
      isDisabled: archived,
    }));
  });

  const handleOnReplace = newIntent =>
    replaceIntent({ selectedIntent: intentToReplace, newIntent });

  const selectableIntents = useMemo(
    () =>
      intents
        .filter(({ id }) => id !== intentToReplace)
        .map(({ id, name, archived }) => ({
          label: name,
          value: id,
          isDisabled: archived,
        })),
    [intents, intentToReplace],
  );

  return (
    <>
      <SelectableList
        currentItemIndex={currentIntentIndex}
        deselectItem={deselectIntent}
        duplicateItem={duplicateIntent}
        ItemComponent={<IntentItem triggerModal={openModal} />}
        items={intents}
        selectedItem={selectedIntent}
        selectItem={selectIntent}
        toggleItemStatus={toggleIntentStatus}
      />
      {intentToReplace && (
        <ReplaceAllModal
          isLoading={isLoading}
          items={selectableIntents}
          loadOptions={fetchIntents}
          type="intent"
          onCancel={closeModal}
          onReplace={handleOnReplace}
        />
      )}
    </>
  );
};

IntentsList.propTypes = {
  currentIntentIndex: PropTypes.number.isRequired,
  deselectIntent: PropTypes.func.isRequired,
  duplicateIntent: PropTypes.func.isRequired,
  intents: PropTypes.arrayOf(intentType).isRequired,
  selectIntent: PropTypes.func.isRequired,
  toggleIntentStatus: PropTypes.func.isRequired,
  selectedIntent: intentType,
};

export default IntentsList;
